import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const LOCALE_STORAGE_KEY = "sielpa:locale";
export const DEFAULT_LOCALE = "ja";
export const SUPPORTED_LOCALES = ["ja", "en"];

const translations = {
  en: {
    locale: {
      label: "Language",
      japanese: "日本語",
      english: "English",
      switchToJapanese: "Switch to Japanese",
      switchToEnglish: "Switch to English"
    },
    common: {
      back: "Back",
      home: "Home",
      open: "Open",
      close: "Close",
      next: "Next",
      previous: "Back",
      loading: "Loading...",
      ready: "Ready",
      wait: "Wait...",
      listening: "Listening...",
      checking: "Checking...",
      score: "Score {score}",
      module: "Module {number}",
      unit: "Unit {number}",
      allUnits: "All Units",
      modules: "{count} modules",
      words: "Words",
      passages: "Passages",
      selected: "Selected",
      wordTarget: "Word target",
      targetWord: "Target word",
      premiumVoice: "Premium voice",
      noItems: "No items"
    },
    levels: {
      bronze: {
        label: "Bronze",
        toolsLabel: "Bronze tools",
        workspace: "Bronze workspace",
        description: "Core vocabulary, pronunciation, and writing practice for early Dictogloss in Action learners."
      },
      silver: {
        label: "Silver",
        toolsLabel: "Silver tools",
        workspace: "Silver workspace",
        description: "Vocabulary, pronunciation, writing, and summarizing practice for intermediate learners."
      },
      gold: {
        label: "Gold",
        toolsLabel: "Gold tools",
        workspace: "Gold workspace",
        description: "Advanced practice for stronger vocabulary, pronunciation, writing, and summarizing."
      }
    },
    tools: {
      vocabulary: {
        label: "Vocabulary",
        title: "Vocabulary Practice",
        subtitle: "Choose your book location and practice vocabulary with feedback.",
        launcherText: "Study words, chunks, meanings, and example sentences.",
        launcherPoints: "Mixed quiz / Writing / Speaking/listening / Vocab Burst"
      },
      pronunciation: {
        label: "Pronunciation",
        title: "Pronunciation Practice",
        subtitle: "Listen, repeat, and compare your pronunciation.",
        launcherText: "Listen, repeat, and check your spoken line.",
        launcherPoints: "Computer voice / Premium Voice / Speech scoring"
      },
      writing: {
        label: "Writing",
        title: "Writing Practice",
        subtitle: "Choose your module, write in the workspace, and check goals as you revise.",
        launcherText: "Draft a response and track live writing goals.",
        launcherPoints: "Word count / Sentence length / Vocabulary goals"
      },
      summarizing: {
        label: "Summarizing",
        title: "Summarizing Practice",
        subtitle: "Choose a passage, write a summary, and check length and copying limits.",
        launcherText: "Read a passage and write a focused summary.",
        launcherPoints: "Source text / Word range / Quality checks"
      }
    },
    shell: {
      backToLevel: "Back to level",
      switchLevel: "Switch level",
      publisher: "SielpA Books",
      managePlus: "Manage Plus",
      levelTitle: "{level} Level",
      levelBadge: "{level} badge"
    },
    home: {
      bannerAlt: "SielpA Books: Dictogloss in Action Practice Tools",
      title: "Choose your level",
      kicker: "SielpA practice tools",
      intro: "Select Bronze, Silver, or Gold to start practice matched to your Dictogloss in Action course book.",
      chooseLevel: "Choose a level",
      startLevel: "Start {level} level practice",
      start: "Start",
      optionalUpgrade: "Optional Plus features",
      plusCopy: "Designed for classes and independent learners who want a quieter, richer practice workspace.",
      removeAdsTitle: "Remove ads",
      removeAdsCopy: "Keep the practice surface focused.",
      premiumVoiceTitle: "Premium Voice",
      premiumVoiceCopy: "Clearer listening and pronunciation support.",
      vocabBurstTitle: "Vocab Burst",
      vocabBurstCopy: "Add fast vocabulary review from your LineSpeak workflow."
    },
    whatsNew: {
      eyebrow: "What's new",
      title: "SielpA is now bilingual",
      languageCopy: "The app now opens in Japanese by default. You can switch to English anytime from the language toggle.",
      plusTitle: "Interested in SielpA Plus?",
      plusCopy: "Ask about Premium Voice, Vocab Burst, and ad-free practice using the inquiry form.",
      optionalCopy: "SielpA Plus is optional. The core textbook practice stays available without it.",
      featureVocabBurst: "Vocab Burst vocabulary practice",
      featurePremiumVoice: "Premium Voice for pronunciation and listening practice",
      featureNoAds: "Ad-free learning",
      viewPlus: "Ask about Plus",
      later: "Later"
    },
    levelHome: {
      chooseTool: "Choose a practice tool",
      vocabBurstNote: "Vocab Burst: ask about Plus",
      premiumVoiceNote: "Premium Voice: ask about Plus"
    },
    moduleSelector: {
      module: "Module",
      unit: "Unit"
    },
    ad: {
      top: "Top advertisement",
      bottom: "Bottom advertisement",
      label: "Advertisement",
      remove: "Remove ads with SielpA Plus"
    },
    footer: {
      kicker: "More English practice",
      title: "More PeraPera Practice",
      linksLabel: "More PeraPera practice links",
      trouble: "Having technical trouble with the new version? Go to the legacy version {link}.",
      troublePrefix: "Having technical trouble with the new version? Go to the legacy version",
      troubleSuffix: ".",
      here: "here",
      navLabel: "Footer links",
      about: "About this app",
      feedback: "Feedback",
      copyright: "© PeraPera 2026. © 2026 SielpA Hub | Designed for seamless learning experiences."
    },
    plus: {
      contactTitle: "Interested in SielpA Plus?",
      contactCopy: "Use the inquiry form to ask about availability and access. Textbook practice stays free.",
      emailUs: "Ask about Plus",
      upgradeTitle: "Interested in SielpA Plus?",
      upgradeCopy: "Get Premium Voice, Vocab Burst practice, and an ad-free workspace.",
      modalCopy: "SielpA Plus adds Premium Voice, more Vocab Burst practice, and an ad-free workspace.",
      renewTitle: "Renew SielpA Plus",
      activeTitle: "SielpA Plus is active",
      upgradeLabel: "SielpA Plus enquiries",
      featuresLabel: "SielpA Plus features",
      noAds: "No ads",
      premiumVoice: "Premium Voice",
      vocabBurst: "Vocab Burst",
      manage: "Manage subscription",
      maybeLater: "Maybe later",
      opening: "Opening...",
      renewPrefix: "Renew ",
      activeCopy: "Ads are hidden and Plus practice features are unlocked on this device.",
      canceledCopy: "This subscription is canceled. Renew now to keep SielpA Plus access.",
      canceledUntil: "Plus access stays active until {date}. Renew now to keep access after that.",
      modalCanceledCopy: "Your previous subscription is canceled. Start a new subscription to keep SielpA Plus access.",
      modalCanceledUntil: "Your previous subscription is canceled. Plus access stays active until {date}. Start a new subscription to keep access after that.",
      featurePremiumVoice: "Premium Voice for clearer listening practice",
      featureVocabBurst: "Vocab Burst: generate examples or a short story from a word, then listen and speak",
      featureNoAds: "No ads while practicing",
      activeLabel: "SielpA Plus active",
      renewPlus: "Ask about Plus",
      defaultNudge: "Ask about SielpA Plus",
      dismiss: "Dismiss",
      statusChecking: "Checking your SielpA Plus status...",
      checkoutNotConnected: "SielpA Plus checkout is not connected yet.",
      paymentsNotConnected: "SielpA Plus payments are not connected to the backend yet.",
      checkoutNotReady: "SielpA Plus checkout is not ready yet.",
      noActive: "No active SielpA Plus subscription was found.",
      activeUntil: "SielpA Plus is active until {date}. Renew to keep access after that.",
      activeCanceled: "SielpA Plus is active, but the subscription is canceled. Renew to keep access.",
      activeOnDevice: "SielpA Plus is active on this device.",
      thanks: "Thanks for subscribing. Activating SielpA Plus...",
      welcomeNotice: "Welcome to SielpA Plus. Your benefits are active.",
      canceledCheckout: "Checkout was canceled. Textbook practice is still available.",
      welcomeTitle: "Welcome to SielpA Plus",
      welcomeCopy: "Your subscription is active. Premium Voice, more Vocab Burst practice, and ad-free practice are ready on this device.",
      welcomeFeatureVoice: "Premium Voice limits increased",
      welcomeFeatureBurst: "Vocab Burst sessions unlocked",
      welcomeFeatureAds: "Advertisements hidden",
      startPracticing: "Start practicing"
    },
    billing: {
      monthly: "Monthly",
      yearly: "Yearly",
      checkoutNotConfigured: "SielpA Plus checkout is not connected yet. Add the Supabase function URL and anon key to the React environment.",
      networkError: "SielpA Plus checkout could not connect. The payment backend may not be deployed yet.",
      requestFailed: "SielpA Plus checkout is not ready yet.",
      urlMissing: "Checkout opened, but Stripe did not return a checkout URL.",
      portalUrlMissing: "The billing portal did not return a URL."
    },
    writing: {
      currentModule: "Current module",
      kicker: "{level} writing",
      prompt: "Write about the topic using vocabulary from the unit.",
      currentModuleValue: "Current module: Module {number}",
      metricsLabel: "Current writing metrics",
      sentenceLength: "Sentence length",
      goalsMet: "Goals met",
      yourDraft: "Your draft",
      workspace: "Writing workspace",
      placeholder: "Begin typing here.",
      liveGoals: "Live goals",
      goals: "Writing goals",
      length: "Length",
      unitVocabulary: "Unit Vocabulary",
      wordVariety: "Word Variety",
      plan: "Plan",
      goalWords: "Goal: {count}+ words",
      goalUnitWords: "Goal: {count}+ unit words",
      goalNumber: "Goal: {count}+",
      goalCttr: "Goal: {count}+ CTTR in legacy app",
      goalDataPending: "Goal data pending",
      goldQualityGoals: "Gold uses quality goals",
      lengthGoalMet: "Length goal met",
      wordsToLengthGoal: "{count} words to length goal",
      sentenceLengthGoalMet: "Sentence length goal met",
      wordsToSentenceGoal: "{count} words to sentence length goal",
      advicePrompt: "Click a goal below to see results and advice.",
      adfreeHint: "Remove ads with SielpA Plus"
    },
    summarizing: {
      currentPassage: "Current passage",
      passage: "Passage {number}",
      kicker: "{level} summarizing",
      prompt: "Summarize the reading passage.",
      writeRange: "Write a clear summary in {range}.",
      loadingPassages: "Loading passages...",
      targetsLabel: "Current passage targets",
      sourceText: "Source text",
      readingPassage: "Reading passage",
      noPassage: "No passage loaded yet.",
      yourSummary: "Your summary",
      workspace: "Draft workspace",
      qualityChecks: "Quality checks",
      goals: "Summarizing goals",
      target: "Target",
      onTrack: "On track",
      revise: "Revise",
      originality: "Originality",
      pending: "Pending",
      copyingPending: "Copying check will return when legacy logic is migrated.",
      copying: "Copying",
      copyingLimit: "Goal: less than 10%",
      advicePrompt: "Click a goal below to see results and advice.",
      rangeWords: "{min}-{max} words",
      loading: "Loading",
      loadingPassage: "Loading passage...",
      wordsToMinimum: "{count} words to minimum",
      wordsOverMaximum: "{count} words over maximum",
      withinRange: "Within target range"
    },
    pronunciation: {
      loadingData: "Loading pronunciation data...",
      loadModuleError: "Could not load module {number}",
      fluencyTooSlow: "That is too slow; try speaking more quickly with fewer pauses.",
      fluencyGoodStart: "That is a good start, but you can improve fluency a little more.",
      fluencyGreat: "That is great fluency.",
      fluencyTooFast: "That is too fast. Slow down a little and focus on pronunciation.",
      noFinalSpeech: "No final speech was captured. Try again in a quiet place and wait until the status says Listening.",
      setupKicker: "{level} pronunciation",
      setupTitle: "Practice setup",
      statusLabel: "Pronunciation practice status",
      lines: "Lines",
      needsSetup: "Needs setup",
      computerVoice: "Computer voice",
      speed: "Speed: {speed}",
      deviceDefault: "Device default",
      britishEnglish: "British English",
      americanEnglish: "American English",
      currentLine: "Current line",
      chooseModule: "Choose a module to load a speaking line.",
      speaking: "Speaking",
      hear: "Hear",
      premiumHear: "Premium Hear",
      stopSpeaking: "Stop Speaking",
      speak: "Speak",
      premiumVoiceOptions: "Premium voice options",
      upgradeMoreVoice: "Ask about more Premium Voice",
      browserAudio: "Browser Audio",
      speakingFeedback: "Speaking",
      line: "Line",
      previousLine: "Previous line",
      nextLine: "Next line",
      feedbackKicker: "Speaking feedback",
      result: "Result",
      accuracy: "Accuracy",
      fluency: "Fluency",
      captured: "Captured",
      speakToScore: "Speak to score",
      syllablesSec: "Syllables/sec",
      finalAsrWords: "Final ASR words",
      waiting: "Waiting",
      missedRed: "The words you missed are written in red.",
      wordCheck: "Word check",
      recognizedSpeech: "Recognized speech",
      transcriptPlaceholder: "Your final recognized speech will appear here after you speak.",
      browserReady: "Computer voice is ready.",
      britishReady: "British computer voice is ready.",
      premiumSelected: "{voice} ({accent}) is selected. Press Premium Hear to listen.",
      speakInstruction: "Press Speak, read the line, then stop to see your score.",
      browserPlaying: "Browser audio playing.",
      browserUnavailable: "Browser audio is not available on this device.",
      browserPlayedInstead: "Browser audio played instead.",
      premiumNeedsConfig: "Premium Voice needs VITE_SIELPA_PLUS_API_BASE_URL and VITE_SUPABASE_ANON_KEY.",
      preparingPremium: "Preparing Premium Voice...",
      premiumPlayed: "{voice} Premium Voice played.",
      premiumRemaining: " {count} Premium Voice plays left today.",
      premiumAudioPlaying: "Premium audio playing.",
      premiumNoAudio: "Premium Voice did not return audio.",
      wordCount: "{correct}/{total} words"
    },
    vocab: {
      loadingData: "Loading vocabulary data...",
      loadModuleError: "Could not load module {number}",
      modes: {
        multipleChoice: "Multiple Choice",
        multipleChoiceSummary: "Choose the best answer.",
        writing: "Writing",
        writingSummary: "Type the missing word.",
        speakingListening: "Speaking and Listening",
        speakingListeningSummary: "Listen, repeat, and check speech.",
        vocabBurst: "Vocab Burst",
        vocabBurstSummary: "Generate and speak new examples."
      },
      chooseBlank: "Choose the word or phrase that best fits the blank.",
      chooseMeaning: "Choose the word or phrase with this meaning.",
      goodJob: "Good job",
      tryAgain: "Try again",
      typeCompleteWord: "Type the complete word",
      niceWork: "Nice work. Move to the next word when you are ready.",
      correctAnswer: "Correct answer: {answer}",
      spellingMatches: "Your spelling matches the target word.",
      studyAnswer: "Study the answer, then try the next word.",
      audioUnavailable: "Audio unavailable",
      browserSpeechUnavailable: "Browser speech is not available on this device.",
      listen: "Listen",
      repeatAndSpeak: "Now repeat the sentence and press Speak.",
      premiumUnavailable: "Premium Voice unavailable",
      premiumLimit: "Premium Voice is limited to {count} characters.",
      premiumNotConnected: "Premium Voice is not connected",
      checkSupabase: "Check the Supabase URL and anon key environment variables.",
      loadingPremium: "Loading Premium Voice...",
      premiumPlayed: "{voice} Premium Voice played.",
      premiumRemaining: " {count} Premium Voice plays left today.",
      freePremiumUsed: "Free Premium Voice plays used today. Email us about SielpA Plus for more Premium Voice.",
      premiumCouldNotPlay: "Premium Voice could not play right now.",
      repeatGenerated: "Now repeat the generated line.",
      heard: "Heard: {text}",
      noSpeech: "No speech was captured. Try again in a quiet place.",
      chooseTarget: "Choose a target word first.",
      burstNotConnected: "Vocab Burst is not connected yet. Textbook practice still works.",
      burstFreeUsed: "Your free Vocab Burst sessions are used for today. Email us about SielpA Plus for more practice.",
      generatingBurst: "Generating Vocab Burst...",
      noBurstLines: "Vocab Burst did not return any practice lines.",
      burstDemo: "Demo Vocab Burst generated locally. Deploy the Supabase function for AI-generated practice.",
      burstGenerated: "Vocab Burst generated.",
      burstSessionUsed: "Vocab Burst session {used} of {limit} used today.",
      burstPlusFeature: "Vocab Burst is a SielpA Plus feature. Email us to ask about Plus access.",
      burstBusy: "Vocab Burst is busy right now. Please wait a moment and try again.",
      burstNetwork: "Vocab Burst backend is not reachable yet. Textbook practice still works.",
      burstUnavailable: "Vocab Burst is unavailable right now. Textbook practice still works.",
      targetInGenerated: "You said the target word in the generated line.",
      speechUnavailable: "Speech unavailable",
      speechError: "Speech error",
      wantMore: "Want more examples with this word?",
      tryVocabBurst: "Try Vocab Burst",
      sentence: "Sentence",
      definition: "Definition",
      completeWord: "Type the complete word.",
      writingAnswerLabel: "Vocabulary writing answer",
      checkAnswer: "Check answer",
      showAnswer: "Show answer",
      targetWordLine: "Listen, then say the sentence. Target word: {word}",
      stop: "Stop",
      transcriptPlaceholder: "Your speech will appear here.",
      burstEnquiry: "Interested in Vocab Burst? Choose Generate to ask about Plus access. Textbook practice stays free.",
      burstReadyPlus: "Vocab Burst is ready with SielpA Plus.",
      burstFreeLeft: "{remaining} of {limit} free Vocab Burst sessions left today.",
      burstGeneratorLabel: "Vocab Burst generator",
      burstHeadline: "Generate, listen, and speak with new examples.",
      burstFlow: "Generate, hear, then speak.",
      noWords: "No words available",
      generate: "Generate Vocab Burst",
      generating: "Generating...",
      unlockMore: "Unlock more Vocab Burst",
      mode: "Mode",
      sentences: "Sentences",
      story: "Story",
      count: "Count",
      length: "Length",
      normal: "Normal",
      short: "Short",
      collocations: "Collocations",
      freeUsedTitle: "You used today's free Vocab Burst sessions.",
      freeUsedCopy: "SielpA Plus adds more Vocab Burst practice, Premium Voice, and no ads.",
      keepPracticingTitle: "Keep practicing with SielpA Plus",
      keepPracticingCopy: "Unlock more Vocab Burst sessions, Premium Voice, and an ad-free workspace.",
      lastFree: "Last free Vocab Burst session today.",
      keepBurst: "Keep Vocab Burst with SielpA Plus",
      ready: "Ready.",
      generatedPractice: "Generated practice",
      targetWordResult: "Target word: {word}",
      setupTitle: "Practice Setup",
      setupCopy: "Choose the book location and practice mode.",
      practiceMode: "Practice mode",
      howPractice: "How would you like to practice?",
      opensFirst: "Multiple Choice opens first.",
      freePerDay: "5 free/day",
      needFresh: "Need fresh examples after textbook practice?",
      moreReady: "More sessions ready",
      leftToday: "{count} left today",
      noItemsAvailable: "No vocabulary items are available for this selection.",
      progress: "{current} of {total}",
      statusLabel: "Vocabulary practice status",
      freePerDayWithCount: "{count} free/day"
    },
    asr: {
      network: "Speech recognition needs an internet connection. Please reconnect and try again.",
      httpsRequired: "Speech recognition requires a secure HTTPS connection. Please open the deployed app over HTTPS and try again.",
      noApi: "Your browser does not support speech recognition. Try Chrome, Safari, or Edge. On iPhone or iPad, Safari is usually the most reliable choice.",
      notAllowed: "Microphone access was denied. Allow microphone access in your browser settings, then try again.",
      serviceNotAllowed: "The speech recognition service is unavailable right now. This can happen in private browsing, on blocked networks, or during temporary service outages.",
      audioCapture: "Your device had trouble hearing you. Check microphone volume, disconnect unused Bluetooth microphones, move closer to the microphone, or try a quieter place.",
      generic: "There was an error with speech recognition: {error}"
    },
    premium: {
      dailyLimit: "Free Premium Voice plays used today. Email us about SielpA Plus for more Premium Voice.",
      hourlyLimit: "Premium Voice is cooling down for this device. Browser audio still works.",
      globalLimit: "Premium Voice is paused today to protect costs. Browser audio still works.",
      tooLong: "Premium Voice is limited to {count} characters. Browser audio played instead.",
      notConfigured: "Premium Voice is not connected yet, so browser audio played instead.",
      unavailable: "Premium Voice is unavailable right now, so browser audio played instead."
    }
  },
  ja: {
    locale: {
      label: "表示言語",
      japanese: "日本語",
      english: "English",
      switchToJapanese: "日本語に切り替える",
      switchToEnglish: "英語に切り替える"
    },
    common: {
      back: "戻る",
      home: "ホーム",
      open: "開く",
      close: "閉じる",
      next: "次へ",
      previous: "戻る",
      loading: "読み込み中...",
      ready: "準備できました",
      wait: "少し待ってください...",
      listening: "聞き取り中...",
      checking: "確認中...",
      score: "スコア {score}",
      module: "Module {number}",
      unit: "Unit {number}",
      allUnits: "すべてのUnit",
      modules: "{count} modules",
      words: "語数",
      passages: "パッセージ",
      selected: "選択中",
      wordTarget: "目標語数",
      targetWord: "練習する語",
      premiumVoice: "Premium Voice",
      noItems: "項目がありません"
    },
    levels: {
      bronze: {
        label: "Bronze",
        toolsLabel: "Bronze の練習",
        workspace: "Bronze ワークスペース",
        description: "Dictogloss in Action の初級学習者向けに、基本語彙・発音・ライティングを練習します。"
      },
      silver: {
        label: "Silver",
        toolsLabel: "Silver の練習",
        workspace: "Silver ワークスペース",
        description: "中級学習者向けに、語彙・発音・ライティング・要約を練習します。"
      },
      gold: {
        label: "Gold",
        toolsLabel: "Gold の練習",
        workspace: "Gold ワークスペース",
        description: "語彙・発音・ライティング・要約をさらに伸ばす上級練習です。"
      }
    },
    tools: {
      vocabulary: {
        label: "Vocabulary",
        title: "Vocabulary Practice",
        subtitle: "教科書の場所を選び、フィードバックを見ながら語彙を練習します。",
        launcherText: "単語、チャンク、意味、例文を練習します。",
        launcherPoints: "選択問題 / ライティング / スピーキング・リスニング / Vocab Burst"
      },
      pronunciation: {
        label: "Pronunciation",
        title: "Pronunciation Practice",
        subtitle: "聞いて、まねして、自分の発音を確認します。",
        launcherText: "英文を聞き、声に出して、発音をチェックします。",
        launcherPoints: "コンピューター音声 / Premium Voice / 音声チェック"
      },
      writing: {
        label: "Writing",
        title: "Writing Practice",
        subtitle: "Module を選び、ワークスペースで英文を書きながら目標を確認します。",
        launcherText: "英文を書き、語数や文の長さなどの目標を確認します。",
        launcherPoints: "語数 / 文の長さ / 語彙目標"
      },
      summarizing: {
        label: "Summarizing",
        title: "Summarizing Practice",
        subtitle: "英文を読み、要約を書き、長さやコピーしすぎを確認します。",
        launcherText: "英文を読んで、ポイントをしぼった要約を書きます。",
        launcherPoints: "本文 / 語数範囲 / チェック"
      }
    },
    shell: {
      backToLevel: "レベル画面に戻る",
      switchLevel: "レベルを切り替える",
      publisher: "SielpA Books",
      managePlus: "Plus を管理",
      levelTitle: "{level} Level",
      levelBadge: "{level} バッジ"
    },
    home: {
      bannerAlt: "SielpA Books: Dictogloss in Action 練習ツール",
      title: "レベルを選びましょう",
      kicker: "SielpA 練習ツール",
      intro: "Dictogloss in Action のコースブックに合わせて、Bronze、Silver、Gold から練習を始めましょう。",
      chooseLevel: "レベルを選ぶ",
      startLevel: "{level} レベルの練習を始める",
      start: "始める",
      optionalUpgrade: "任意の Plus 機能",
      plusCopy: "授業でも自習でも、より集中しやすい練習環境を使いたい学習者向けです。",
      removeAdsTitle: "広告を非表示",
      removeAdsCopy: "練習画面に集中できます。",
      premiumVoiceTitle: "Premium Voice",
      premiumVoiceCopy: "より聞き取りやすい音声で発音練習をサポートします。",
      vocabBurstTitle: "Vocab Burst",
      vocabBurstCopy: "LineSpeak の流れからすばやく語彙復習を追加できます。"
    },
    whatsNew: {
      eyebrow: "新機能のお知らせ",
      title: "SielpA が日本語に対応しました",
      languageCopy: "画面の説明やボタンが日本語で表示されるようになりました。右上の切り替えから English に変更できます。",
      plusTitle: "SielpA Plus に興味がありますか？",
      plusCopy: "フォームで Premium Voice、Vocab Burst、広告なしの練習についてお問い合わせください。",
      optionalCopy: "SielpA Plus は任意です。教科書の基本練習は登録しなくてもそのまま使えます。",
      featureVocabBurst: "Vocab Burst で語彙練習",
      featurePremiumVoice: "Premium Voice で発音・リスニング練習",
      featureNoAds: "広告なしで集中して学習",
      viewPlus: "Plus について問い合わせる",
      later: "あとで"
    },
    levelHome: {
      chooseTool: "練習ツールを選びましょう",
      vocabBurstNote: "Vocab Burst: Plus について問い合わせる",
      premiumVoiceNote: "Premium Voice: Plus について問い合わせる"
    },
    moduleSelector: {
      module: "Module",
      unit: "Unit"
    },
    ad: {
      top: "上部広告",
      bottom: "下部広告",
      label: "広告",
      remove: "SielpA Plus で広告を非表示"
    },
    footer: {
      kicker: "ほかの英語練習",
      title: "More PeraPera Practice",
      linksLabel: "More PeraPera Practice へのリンク",
      trouble: "新しいバージョンで技術的な問題がある場合は、旧バージョンを {link} から開けます。",
      troublePrefix: "新しいバージョンで技術的な問題がある場合は、旧バージョンを",
      troubleSuffix: "から開けます。",
      here: "こちら",
      navLabel: "フッターリンク",
      about: "このアプリについて",
      feedback: "フィードバック",
      copyright: "© PeraPera 2026. © 2026 SielpA Hub | 学習しやすい体験のために設計されています。"
    },
    plus: {
      contactTitle: "SielpA Plus に興味がありますか？",
      contactCopy: "フォームで Plus の提供状況と利用方法をお問い合わせください。教科書の練習は無料のまま使えます。",
      emailUs: "Plus について問い合わせる",
      upgradeTitle: "SielpA Plus に興味がありますか？",
      upgradeCopy: "Premium Voice、Vocab Burst、広告なしの練習画面を使えます。",
      modalCopy: "SielpA Plus では、Premium Voice、より多くの Vocab Burst、広告なしの練習画面を使えます。",
      renewTitle: "SielpA Plus を更新",
      activeTitle: "SielpA Plus は有効です",
      upgradeLabel: "SielpA Plus のお問い合わせ",
      featuresLabel: "SielpA Plus の機能",
      noAds: "広告なし",
      premiumVoice: "Premium Voice",
      vocabBurst: "Vocab Burst",
      manage: "サブスクリプションを管理",
      maybeLater: "あとで",
      opening: "開いています...",
      renewPrefix: "更新: ",
      activeCopy: "この端末では広告が非表示になり、Plus の練習機能が使えます。",
      canceledCopy: "このサブスクリプションはキャンセル済みです。SielpA Plus を続けるには更新してください。",
      canceledUntil: "{date} まで Plus を使えます。その後も使うには更新してください。",
      modalCanceledCopy: "前のサブスクリプションはキャンセル済みです。SielpA Plus を続けるには新しいサブスクリプションを開始してください。",
      modalCanceledUntil: "{date} まで Plus を使えます。その後も使うには新しいサブスクリプションを開始してください。",
      featurePremiumVoice: "より聞き取りやすい Premium Voice",
      featureVocabBurst: "Vocab Burst：単語から例文や短いストーリーを生成し、聞いて話す練習",
      featureNoAds: "練習中の広告なし",
      activeLabel: "SielpA Plus 有効",
      renewPlus: "Plus について問い合わせる",
      defaultNudge: "SielpA Plus について問い合わせる",
      dismiss: "閉じる",
      statusChecking: "SielpA Plus の状態を確認しています...",
      checkoutNotConnected: "SielpA Plus の決済はまだ接続されていません。",
      paymentsNotConnected: "SielpA Plus の支払いバックエンドはまだ接続されていません。",
      checkoutNotReady: "SielpA Plus の決済はまだ準備できていません。",
      noActive: "有効な SielpA Plus サブスクリプションは見つかりませんでした。",
      activeUntil: "{date} まで SielpA Plus は有効です。その後も使うには更新してください。",
      activeCanceled: "SielpA Plus は有効ですが、サブスクリプションはキャンセル済みです。続けるには更新してください。",
      activeOnDevice: "この端末で SielpA Plus が有効です。",
      thanks: "ご登録ありがとうございます。SielpA Plus を有効化しています...",
      welcomeNotice: "SielpA Plus へようこそ。特典が有効になりました。",
      canceledCheckout: "Checkout はキャンセルされました。教科書練習はそのまま使えます。",
      welcomeTitle: "SielpA Plus へようこそ",
      welcomeCopy: "サブスクリプションが有効です。Premium Voice、Vocab Burst、広告なしの練習を使えます。",
      welcomeFeatureVoice: "Premium Voice の上限が増えました",
      welcomeFeatureBurst: "Vocab Burst が使えます",
      welcomeFeatureAds: "広告が非表示になりました",
      startPracticing: "練習を始める"
    },
    billing: {
      monthly: "月額",
      yearly: "年額",
      checkoutNotConfigured: "SielpA Plus の決済はまだ接続されていません。React の環境変数に Supabase function URL と anon key を追加してください。",
      networkError: "SielpA Plus の決済に接続できません。支払いバックエンドがまだデプロイされていない可能性があります。",
      requestFailed: "SielpA Plus の決済はまだ準備できていません。",
      urlMissing: "Checkout は開始されましたが、Stripe から Checkout URL が返りませんでした。",
      portalUrlMissing: "Billing Portal URL が返りませんでした。"
    },
    writing: {
      currentModule: "現在のModule",
      kicker: "{level} writing",
      prompt: "Unit の語彙を使って、トピックについて英語で書きましょう。",
      currentModuleValue: "現在のModule: Module {number}",
      metricsLabel: "現在のライティング指標",
      sentenceLength: "文の長さ",
      goalsMet: "達成した目標",
      yourDraft: "あなたの下書き",
      workspace: "ライティングワークスペース",
      placeholder: "ここに入力してください。",
      liveGoals: "現在の目標",
      goals: "ライティング目標",
      length: "長さ",
      unitVocabulary: "Unit 語彙",
      wordVariety: "語彙の多様性",
      plan: "計画",
      goalWords: "目標: {count}語以上",
      goalUnitWords: "目標: Unit 語彙 {count}語以上",
      goalNumber: "目標: {count}以上",
      goalCttr: "目標: 旧アプリ基準で CTTR {count}以上",
      goalDataPending: "目標データは準備中です",
      goldQualityGoals: "Gold は質の目標を使います",
      lengthGoalMet: "長さの目標を達成しました",
      wordsToLengthGoal: "長さの目標まであと {count} 語",
      sentenceLengthGoalMet: "文の長さの目標を達成しました",
      wordsToSentenceGoal: "文の長さの目標まであと {count} 語",
      advicePrompt: "下の目標をクリックすると、結果とアドバイスを確認できます。",
      adfreeHint: "SielpA Plus で広告を非表示"
    },
    summarizing: {
      currentPassage: "現在のパッセージ",
      passage: "Passage {number}",
      kicker: "{level} summarizing",
      prompt: "リーディング本文を要約しましょう。",
      writeRange: "{range} で、わかりやすい要約を書きましょう。",
      loadingPassages: "パッセージを読み込んでいます...",
      targetsLabel: "現在のパッセージ目標",
      sourceText: "本文",
      readingPassage: "リーディング本文",
      noPassage: "まだパッセージが読み込まれていません。",
      yourSummary: "あなたの要約",
      workspace: "下書きワークスペース",
      qualityChecks: "チェック項目",
      goals: "要約の目標",
      target: "目標",
      onTrack: "順調",
      revise: "修正",
      originality: "オリジナリティ",
      pending: "準備中",
      copyingPending: "コピー判定は旧アプリのロジック移行後に戻ります。",
      copying: "コピー",
      copyingLimit: "目標: 10% 未満",
      advicePrompt: "下の目標をクリックすると、結果とアドバイスを確認できます。",
      rangeWords: "{min}-{max} 語",
      loading: "読み込み中",
      loadingPassage: "パッセージを読み込んでいます...",
      wordsToMinimum: "最小語数まであと {count} 語",
      wordsOverMaximum: "最大語数を {count} 語超えています",
      withinRange: "目標範囲内です"
    },
    pronunciation: {
      loadingData: "発音データを読み込んでいます...",
      loadModuleError: "Module {number} を読み込めませんでした",
      fluencyTooSlow: "少し遅すぎます。間を少なくして、もう少し速く話してみましょう。",
      fluencyGoodStart: "よいスタートです。流暢さをもう少し伸ばせます。",
      fluencyGreat: "とてもよい流暢さです。",
      fluencyTooFast: "少し速すぎます。少しゆっくり、発音に集中しましょう。",
      noFinalSpeech: "最後の音声が取得できませんでした。静かな場所で、表示が「聞き取り中」になってからもう一度試してください。",
      setupKicker: "{level} pronunciation",
      setupTitle: "練習設定",
      statusLabel: "発音練習の状態",
      lines: "英文",
      needsSetup: "設定が必要",
      computerVoice: "コンピューター音声",
      speed: "速さ: {speed}",
      deviceDefault: "端末の標準音声",
      britishEnglish: "イギリス英語",
      americanEnglish: "アメリカ英語",
      currentLine: "現在の英文",
      chooseModule: "Module を選ぶと、スピーキング用の英文が表示されます。",
      speaking: "スピーキング",
      hear: "聞く",
      premiumHear: "Premium で聞く",
      stopSpeaking: "録音を止める",
      speak: "話す",
      premiumVoiceOptions: "Premium Voice の選択",
      upgradeMoreVoice: "Premium Voice について問い合わせる",
      browserAudio: "通常音声",
      speakingFeedback: "スピーキング",
      line: "英文",
      previousLine: "前の英文",
      nextLine: "次の英文",
      feedbackKicker: "スピーキングフィードバック",
      result: "結果",
      accuracy: "正確さ",
      fluency: "流暢さ",
      captured: "認識語数",
      speakToScore: "話すとスコアが出ます",
      syllablesSec: "音節/秒",
      finalAsrWords: "最終認識語",
      waiting: "待機中",
      missedRed: "言えなかった語は赤で表示されます。",
      wordCheck: "語のチェック",
      recognizedSpeech: "認識された音声",
      transcriptPlaceholder: "話したあと、認識された音声がここに表示されます。",
      browserReady: "コンピューター音声を使えます。",
      britishReady: "イギリス英語のコンピューター音声を使えます。",
      premiumSelected: "{voice}（{accent}）が選択されています。「Premium で聞く」を押すと聞けます。",
      speakInstruction: "「話す」を押して英文を読み、止めるとスコアが出ます。",
      browserPlaying: "通常音声を再生しています。",
      browserUnavailable: "この端末では通常音声を使えません。",
      browserPlayedInstead: "代わりに通常音声を再生しました。",
      premiumNeedsConfig: "Premium Voice には VITE_SIELPA_PLUS_API_BASE_URL と VITE_SUPABASE_ANON_KEY が必要です。",
      preparingPremium: "Premium Voice を準備しています...",
      premiumPlayed: "{voice} の Premium Voice を再生しました。",
      premiumRemaining: " 今日あと {count} 回 Premium Voice を使えます。",
      premiumAudioPlaying: "Premium Voice を再生しています。",
      premiumNoAudio: "Premium Voice の音声が返りませんでした。",
      wordCount: "{correct}/{total} 語"
    },
    vocab: {
      loadingData: "語彙データを読み込んでいます...",
      loadModuleError: "Module {number} を読み込めませんでした",
      modes: {
        multipleChoice: "選択問題",
        multipleChoiceSummary: "最もよい答えを選びます。",
        writing: "ライティング",
        writingSummary: "空所に入る語を書きます。",
        speakingListening: "スピーキング・リスニング",
        speakingListeningSummary: "聞いて、まねして、音声を確認します。",
        vocabBurst: "Vocab Burst",
        vocabBurstSummary: "新しい例文を作って、聞いて、話します。"
      },
      chooseBlank: "空所に最も合う語句を選びましょう。",
      chooseMeaning: "この意味に合う語句を選びましょう。",
      goodJob: "よくできました",
      tryAgain: "もう一度",
      typeCompleteWord: "語全体を入力しましょう",
      niceWork: "よくできました。準備ができたら次の語へ進みましょう。",
      correctAnswer: "正解: {answer}",
      spellingMatches: "つづりが正解と一致しています。",
      studyAnswer: "答えを確認してから、次の語に進みましょう。",
      audioUnavailable: "音声を使えません",
      browserSpeechUnavailable: "この端末ではブラウザ音声を使えません。",
      listen: "聞く",
      repeatAndSpeak: "次に文をリピートして、「話す」を押してください。",
      premiumUnavailable: "Premium Voice を使えません",
      premiumLimit: "Premium Voice は {count} 文字までです。",
      premiumNotConnected: "Premium Voice は接続されていません",
      checkSupabase: "Supabase URL と anon key の環境変数を確認してください。",
      loadingPremium: "Premium Voice を読み込んでいます...",
      premiumPlayed: "{voice} の Premium Voice を再生しました。",
      premiumRemaining: " 今日あと {count} 回 Premium Voice を使えます。",
      freePremiumUsed: "今日の無料 Premium Voice は使い切りました。もっと使いたい場合は、SielpA Plus についてメールでお問い合わせください。",
      premiumCouldNotPlay: "今は Premium Voice を再生できません。",
      repeatGenerated: "次に生成された文をリピートしましょう。",
      heard: "聞き取った音声: {text}",
      noSpeech: "音声を取得できませんでした。静かな場所でもう一度試してください。",
      chooseTarget: "先に練習する語を選んでください。",
      burstNotConnected: "Vocab Burst はまだ接続されていません。教科書練習はそのまま使えます。",
      burstFreeUsed: "今日の無料 Vocab Burst は使い切りました。もっと練習したい場合は、SielpA Plus についてメールでお問い合わせください。",
      generatingBurst: "Vocab Burst を生成しています...",
      noBurstLines: "Vocab Burst の練習文が返りませんでした。",
      burstDemo: "デモ用の Vocab Burst をこの端末で生成しました。AI生成には Supabase function をデプロイしてください。",
      burstGenerated: "Vocab Burst を生成しました。",
      burstSessionUsed: "今日の Vocab Burst {used}/{limit} 回を使いました。",
      burstPlusFeature: "Vocab Burst は SielpA Plus の機能です。利用について、メールでお問い合わせください。",
      burstBusy: "Vocab Burst が混み合っています。少し待ってからもう一度試してください。",
      burstNetwork: "Vocab Burst のバックエンドに接続できません。教科書練習はそのまま使えます。",
      burstUnavailable: "今は Vocab Burst を使えません。教科書練習はそのまま使えます。",
      targetInGenerated: "生成された文の中でターゲット語を言えました。",
      speechUnavailable: "音声認識を使えません",
      speechError: "音声認識エラー",
      wantMore: "この語の例文をもっと練習しますか？",
      tryVocabBurst: "Vocab Burst を試す",
      sentence: "例文",
      definition: "意味",
      completeWord: "語全体を入力しましょう。",
      writingAnswerLabel: "語彙ライティングの答え",
      checkAnswer: "答えを確認",
      showAnswer: "答えを見る",
      targetWordLine: "聞いてから英文を言いましょう。ターゲット語: {word}",
      stop: "止める",
      transcriptPlaceholder: "話した音声がここに表示されます。",
      burstEnquiry: "Vocab Burst に興味がある方は「Vocab Burst を生成」から Plus についてお問い合わせください。教科書の練習は無料のままです。",
      burstReadyPlus: "SielpA Plus で Vocab Burst を使えます。",
      burstFreeLeft: "今日の無料 Vocab Burst は残り {remaining}/{limit} 回です。",
      burstGeneratorLabel: "Vocab Burst 生成ツール",
      burstHeadline: "新しい例文を作り、聞いて、話しましょう。",
      burstFlow: "生成 → 聞く → 話す",
      noWords: "語がありません",
      generate: "Vocab Burst を生成",
      generating: "生成中...",
      unlockMore: "Vocab Burst をもっと使う",
      mode: "モード",
      sentences: "例文",
      story: "ストーリー",
      count: "数",
      length: "長さ",
      normal: "標準",
      short: "短め",
      collocations: "コロケーション",
      freeUsedTitle: "今日の無料 Vocab Burst は使い切りました。",
      freeUsedCopy: "SielpA Plus では、Vocab Burst、Premium Voice、広告なしをもっと使えます。",
      keepPracticingTitle: "SielpA Plus で練習を続ける",
      keepPracticingCopy: "Vocab Burst、Premium Voice、広告なしのワークスペースを使えます。",
      lastFree: "今日最後の無料 Vocab Burst です。",
      keepBurst: "SielpA Plus で Vocab Burst を続ける",
      ready: "準備できました。",
      generatedPractice: "生成された練習",
      targetWordResult: "ターゲット語: {word}",
      setupTitle: "練習設定",
      setupCopy: "教科書の場所と練習モードを選びます。",
      practiceMode: "練習モード",
      howPractice: "どの方法で練習しますか？",
      opensFirst: "最初は選択問題が開きます。",
      freePerDay: "1日5回無料",
      needFresh: "教科書練習のあと、新しい例文も練習しますか？",
      moreReady: "追加セッションを使えます",
      leftToday: "今日あと {count} 回",
      noItemsAvailable: "この選択で使える語彙項目がありません。",
      progress: "{current}/{total}",
      statusLabel: "語彙練習の状態",
      freePerDayWithCount: "1日{count}回無料"
    },
    asr: {
      network: "音声認識にはインターネット接続が必要です。接続してからもう一度試してください。",
      httpsRequired: "音声認識には安全な HTTPS 接続が必要です。デプロイ済みアプリを HTTPS で開いてください。",
      noApi: "このブラウザは音声認識に対応していません。Chrome、Safari、Edge を試してください。iPhone や iPad では Safari が最も安定します。",
      notAllowed: "マイクの使用が拒否されました。ブラウザ設定でマイクを許可してから、もう一度試してください。",
      serviceNotAllowed: "現在、音声認識サービスを使えません。プライベートブラウズ、制限されたネットワーク、一時的な障害などで起こることがあります。",
      audioCapture: "端末が音声をうまく聞き取れませんでした。マイク音量、Bluetooth マイク、マイクとの距離、周囲の静かさを確認してください。",
      generic: "音声認識でエラーが発生しました: {error}"
    },
    premium: {
      dailyLimit: "今日の無料 Premium Voice は使い切りました。もっと使いたい場合は、SielpA Plus についてメールでお問い合わせください。",
      hourlyLimit: "この端末では Premium Voice が一時的に制限されています。通常音声は使えます。",
      globalLimit: "費用保護のため、本日の Premium Voice は一時停止しています。通常音声は使えます。",
      tooLong: "Premium Voice は {count} 文字までです。代わりに通常音声を再生しました。",
      notConfigured: "Premium Voice はまだ接続されていないため、通常音声を再生しました。",
      unavailable: "今は Premium Voice を使えないため、通常音声を再生しました。"
    }
  }
};

function getPathValue(source, path) {
  return path.split(".").reduce((value, part) => value && value[part], source);
}

function interpolate(template, params = {}) {
  return String(template).replace(/\{(\w+)\}/g, (_, key) => params[key] ?? "");
}

function normalizeLocale(value) {
  return SUPPORTED_LOCALES.includes(value) ? value : DEFAULT_LOCALE;
}

function readStoredLocale() {
  try {
    return normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return DEFAULT_LOCALE;
  }
}

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(readStoredLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((nextLocale) => {
    const normalized = normalizeLocale(nextLocale);
    setLocaleState(normalized);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
    } catch {
      // The in-memory locale still works when storage is unavailable.
    }
  }, []);

  const t = useCallback((key, params, fallback) => {
    const value = getPathValue(translations[locale], key)
      ?? getPathValue(translations.en, key)
      ?? fallback
      ?? key;
    return interpolate(value, params);
  }, [locale]);

  const formatDate = useCallback((value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(locale === "ja" ? "ja-JP" : "en-US", {
      year: "numeric",
      month: locale === "ja" ? "numeric" : "long",
      day: "numeric"
    }).format(date);
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t, formatDate }), [formatDate, locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useI18n() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("useI18n must be used inside LocaleProvider.");
  return context;
}
